/**
 * Guest transfer page ▸ the layout (owner, 2026-10-29: "make it more
 * customizable, like a proper CRM ... a WYSIWYG editor, WordPress style").
 * The page is a list of BLOCKS from a fixed set; a block is data that one
 * fixed component renders (TransferBlocks.tsx). Mirrors
 * api/Modules/GuestPortal/TransferPageBlocks.cs, which validates every save
 * and is the authority.
 *
 * The payload carries the layout already resolved (`view.layout`: the agent's
 * own list or everyone's, each link row's pills filled in). Without one (an
 * older API, the tests) the page derives it from the Guest Portal switches
 * exactly as the server does and fills the pills from what it always had:
 * the built-in cities, the agent's countries, the tenant's pills.
 */
import DOMPurify from 'dompurify';
import {
  RECOS,
  pagesFor,
  recosFor,
  sectionOn,
  blockOrder,
  type PublicGuestPortal,
  type PublicTransferView,
} from './publicTransfer.api';

export type TransferBlockType =
  | 'header'
  | 'logo'
  | 'hero'
  | 'services'
  | 'meetings'
  | 'files'
  | 'pills'
  | 'contact'
  | 'richText'
  | 'image'
  | 'button'
  | 'divider'
  | 'faq';

export type ServiceColumn = 'date' | 'pax' | 'time' | 'pickup' | 'dropoff' | 'flight' | 'product';
export const SERVICE_COLUMNS: readonly { id: ServiceColumn; label: string }[] = [
  { id: 'date', label: 'Date' },
  { id: 'pax', label: 'Pax' },
  { id: 'time', label: 'Time' },
  { id: 'pickup', label: 'Pick-up' },
  { id: 'dropoff', label: 'Drop-off' },
  { id: 'flight', label: 'Flight' },
  { id: 'product', label: 'Product' },
];

/** One pill of a link row, as the server fills it (or the page, from its legacy lists). */
export interface Pill {
  label: string;
  href: string;
  /** 'my' / 'sg' follows the agent's destinations; null is shown to everyone. */
  country: string | null;
}

export type PillItemKind = 'guides' | 'tours' | 'places' | 'pages' | 'destinations' | 'driver' | 'feedback' | 'link';
export interface PillItem {
  kind: PillItemKind;
  /** destinations: what the pills lead to. */
  target?: 'places' | 'tours';
  /** driver, feedback, link. */
  label?: string;
  /** link. */
  href?: string;
  style?: 'plain' | 'accent';
  /** Filled by the API (guides, tours, places, pages, destinations). */
  pills?: Pill[];
}
export interface PillRow {
  id: string;
  label?: string;
  items: PillItem[];
}

export interface ContactDesk {
  id: string;
  label: string;
  phone?: string;
  email?: string;
  country?: 'my' | 'sg';
}

export interface BlockProps {
  header: Record<string, never>;
  logo: { source: 'company' | 'agent' | 'url'; url?: string; alt?: string; height: number; align: 'left' | 'center' | 'right' };
  hero: { title: string; subtitle?: string; showRef: boolean };
  services: { showPeriodNote: boolean; columns: ServiceColumn[] };
  meetings: { title: string; subtitle?: string };
  files: { title: string };
  pills: { heading?: string; rows: PillRow[] };
  contact: { intro?: string; desks: 'auto' | ContactDesk[]; showDesks: boolean; showSignOff: boolean; team?: string };
  richText: { html: string };
  image: { url: string; alt?: string; caption?: string; width: 'content' | 'full' };
  button: { label: string; href: string; style: 'primary' | 'secondary'; align: 'left' | 'center' | 'right' };
  divider: { space: 'line' | 'small' | 'medium' | 'large' };
  faq: { title?: string; items: { q: string; a: string }[] };
}

export type TransferBlock<T extends TransferBlockType = TransferBlockType> = {
  [K in T]: { id: string; type: K; visible: boolean; props: BlockProps[K] };
}[T];

export interface TransferLayoutDocument {
  version: 1;
  blocks: TransferBlock[];
  variants?: { byAgent: Record<string, { blocks: TransferBlock[] }> };
}

/** `view.layout`: the blocks of THIS booking, filled, and whether an agent's own list was picked. */
export interface ResolvedLayout {
  blocks: TransferBlock[];
  variant?: boolean;
}

// ── The words the page has always printed ────────────────────────────────

export const DEFAULT_TITLE = 'Your upcoming transfers & excursions';
export const DEFAULT_MEETINGS_TITLE = 'Your Scheduled Welcome Meetings';
export const DEFAULT_MEETINGS_SUBTITLE = 'with our Representatives';
export const DEFAULT_FILES_TITLE = 'Your Documents';
export const DEFAULT_EXPLORE_HEADING = 'Explore & Prepare';
export const DEFAULT_CONTACT_INTRO =
  'Should you require any further information or assistance, please do not hesitate to contact us.';
export const DEFAULT_TEAM = 'Customer Service Team';
export const DEFAULT_DRIVER_LABEL = 'Where to Find Your Driver';
export const DEFAULT_FEEDBACK_LABEL = 'Rate Your Trip';

/** The guide's countries, as the "General Information" pills have always printed them (api PublicGuideService.GuideCountryPills). */
export const GUIDE_PILLS: readonly Pill[] = [
  { label: '🇲🇾 Malaysia', href: '/guide/malaysia', country: 'my' },
  { label: '🇸🇬 Singapore', href: '/guide/singapore', country: 'sg' },
];

/**
 * The layout the Guest Portal switches describe (api TransferPageBlocks.FromSettings):
 * with no portal block at all, today's page, block for block.
 */
export function layoutFromPortal(portal: PublicGuestPortal | null | undefined): TransferLayoutDocument {
  const on = (id: Parameters<typeof sectionOn>[1]) => sectionOn(portal, id);
  const rows: PillRow[] = [];
  const general: PillItem[] = [{ kind: 'guides' }];
  if (on('driverLink')) general.push({ kind: 'driver', label: DEFAULT_DRIVER_LABEL });
  rows.push({ id: 'general', label: 'General Information', items: general });
  if (on('recommendations')) {
    rows.push({ id: 'tours', label: 'Optional Excursions', items: [{ kind: 'tours' }] });
    rows.push({ id: 'places', label: 'Local Recommendations', items: [{ kind: 'places' }] });
  }
  if (on('customPages')) rows.push({ id: 'pages', label: 'Good to Know', items: [{ kind: 'pages' }] });
  if (on('feedbackLink')) rows.push({ id: 'feedback', label: 'Tell Us How It Went', items: [{ kind: 'feedback', label: DEFAULT_FEEDBACK_LABEL }] });

  const body: Record<string, TransferBlock> = {
    services: { id: 'services', type: 'services', visible: on('services'), props: { showPeriodNote: true, columns: SERVICE_COLUMNS.map((c) => c.id) } },
    meetingPoint: { id: 'meetings', type: 'meetings', visible: on('meetingPoint'), props: { title: DEFAULT_MEETINGS_TITLE, subtitle: DEFAULT_MEETINGS_SUBTITLE } },
    explore: { id: 'explore', type: 'pills', visible: on('explore'), props: { heading: DEFAULT_EXPLORE_HEADING, rows } },
    files: { id: 'files', type: 'files', visible: on('files'), props: { title: DEFAULT_FILES_TITLE } },
    contact: {
      id: 'contact',
      type: 'contact',
      visible: true,
      props: { intro: DEFAULT_CONTACT_INTRO, desks: 'auto', showDesks: on('contact'), showSignOff: true, team: DEFAULT_TEAM },
    },
  };
  return {
    version: 1,
    blocks: [
      { id: 'header', type: 'header', visible: on('header'), props: {} },
      { id: 'hero', type: 'hero', visible: true, props: { title: DEFAULT_TITLE, showRef: true } },
      ...blockOrder(portal).map((id) => body[id]!),
    ],
  };
}

/** Today's page as blocks: no Guest Portal row at all. */
export function defaultLayout(): TransferLayoutDocument {
  return layoutFromPortal(null);
}

/**
 * The blocks a view renders: the API's resolved layout, else the one its
 * portal switches describe. A pill item the API did not fill (an older API,
 * or a page without a layout) gets its pills from the view's own lists, so
 * the page shows exactly what it always has.
 */
export function blocksFor(view: PublicTransferView): TransferBlock[] {
  const blocks = view.layout?.blocks ?? layoutFromPortal(view.portal).blocks;
  return blocks.map((b) => (b.type === 'pills' ? fillPills(b, view) : b));
}

function fillPills(block: TransferBlock<'pills'>, view: PublicTransferView): TransferBlock<'pills'> {
  const portal = view.portal;
  const recos = sectionOn(portal, 'recommendations');
  return {
    ...block,
    props: {
      ...block.props,
      rows: block.props.rows.map((row) => ({
        ...row,
        items: row.items.map((item) => {
          if (item.pills) return item;
          switch (item.kind) {
            case 'guides':
              return { ...item, pills: [...GUIDE_PILLS] };
            case 'tours':
              return { ...item, pills: (recos ? recosFor(null, portal?.tours ?? null) : []).map((r) => ({ label: r.name, href: `/tours/${r.slug}`, country: r.country })) };
            case 'places':
              return { ...item, pills: (recos ? recosFor(null, portal?.places ?? null) : []).map((r) => ({ label: r.name, href: `/places/${r.slug}`, country: r.country })) };
            case 'pages':
              return {
                ...item,
                pills: pagesFor(null, portal?.pages).map((p) => ({ label: p.title, href: `/p/${p.slug}`, country: p.countries?.length === 1 ? p.countries[0]! : null })),
              };
            case 'destinations':
              // Only the API knows the tenant's destinations; without it the built-in cities stand in.
              return { ...item, pills: RECOS.map((r) => ({ label: r.name, href: `/${item.target ?? 'places'}/${r.slug}`, country: r.country })) };
            default:
              return item;
          }
        }),
      })),
    },
  };
}

/**
 * The pills an agent's guests see: its country's, and those for everyone
 * (the rule recosFor and pagesFor always applied to the fixed rows).
 */
export function pillsForRegion(pills: readonly Pill[], region: string | null | undefined): Pill[] {
  return pills.filter((p) => !p.country || !region || p.country === region);
}

// ── Rich text (the one HTML block) ─────────────────────────────────────────

const RICH_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'hr', 'a'];

/**
 * The API rebuilt the HTML from its allow-list on save and on read; the page
 * runs DOMPurify over it once more before painting (defence in depth, and the
 * editor's own preview goes through here too).
 */
export function safeRichHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TAGS,
    ALLOWED_ATTR: ['href', 'rel', 'target'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|\/(?!\/))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'img', 'svg'],
    FORBID_ATTR: ['style', 'class', 'id'],
  });
}

// ── The editor's palette ───────────────────────────────────────────────────

export const BLOCK_KINDS: readonly { type: TransferBlockType; label: string; hint: string; single?: boolean }[] = [
  { type: 'header', label: 'Logos', hint: 'Your logo, or the agent’s and its co-brand, as the agent’s look decides', single: true },
  { type: 'logo', label: 'Logo', hint: 'One logo: yours, the agent’s, or any image' },
  { type: 'hero', label: 'Heading', hint: 'The page title and the booking reference', single: true },
  { type: 'services', label: 'Transfer table', hint: 'Every transfer and excursion of the booking; choose the columns', single: true },
  { type: 'meetings', label: 'Welcome meetings', hint: 'When and where to meet the representative', single: true },
  { type: 'files', label: 'Documents', hint: 'Vouchers and files the team shared with the booking', single: true },
  { type: 'pills', label: 'Links', hint: 'Rows of pills: countries, destinations, excursions, your guest pages, any link' },
  { type: 'contact', label: 'Hotline', hint: 'Phone, WhatsApp and email, your sign-off; alone it is a page' },
  { type: 'richText', label: 'Text', hint: 'Paragraphs with headings, bold, lists and links' },
  { type: 'image', label: 'Image', hint: 'One picture with a caption' },
  { type: 'button', label: 'Button', hint: 'A link to a guest page, a website, a phone or an email' },
  { type: 'divider', label: 'Divider', hint: 'A line, or some air between parts' },
  { type: 'faq', label: 'Questions', hint: 'Questions guests open one by one' },
];

export const PILL_ITEM_KINDS: readonly { kind: PillItemKind; label: string; hint: string }[] = [
  { kind: 'guides', label: 'Country guides', hint: 'One pill per country of the travel guide' },
  { kind: 'destinations', label: 'Destinations', hint: 'One pill per destination you publish, to its recommendations or its tours' },
  { kind: 'tours', label: 'Optional excursions', hint: 'The destinations with optional tours' },
  { kind: 'places', label: 'Local recommendations', hint: 'The destinations with local recommendations' },
  { kind: 'pages', label: 'Your guest pages', hint: 'The pages you listed in Guest Portal ▸ Pages' },
  { kind: 'driver', label: 'Where to find your driver', hint: 'The pick-up points page' },
  { kind: 'feedback', label: 'Rate your trip', hint: 'The feedback form on your guest site' },
  { kind: 'link', label: 'A link', hint: 'Any address: a guest page, a website, a phone or an email' },
];

/** A short random id ([a-z0-9], as the API accepts). */
export function newBlockId(): string {
  return Math.random().toString(36).slice(2, 12).replace(/[^a-z0-9]/g, '') || 'b' + Date.now().toString(36);
}

/** A fresh block of a kind, ready to fill in. */
export function newBlock(type: TransferBlockType): TransferBlock {
  const id = newBlockId();
  switch (type) {
    case 'header':
      return { id, type, visible: true, props: {} };
    case 'logo':
      return { id, type, visible: true, props: { source: 'company', height: 42, align: 'center' } };
    case 'hero':
      return { id, type, visible: true, props: { title: DEFAULT_TITLE, showRef: true } };
    case 'services':
      return { id, type, visible: true, props: { showPeriodNote: true, columns: SERVICE_COLUMNS.map((c) => c.id) } };
    case 'meetings':
      return { id, type, visible: true, props: { title: DEFAULT_MEETINGS_TITLE, subtitle: DEFAULT_MEETINGS_SUBTITLE } };
    case 'files':
      return { id, type, visible: true, props: { title: DEFAULT_FILES_TITLE } };
    case 'pills':
      return { id, type, visible: true, props: { rows: [{ id: newBlockId(), label: '', items: [{ kind: 'guides' }] }] } };
    case 'contact':
      return { id, type, visible: true, props: { intro: DEFAULT_CONTACT_INTRO, desks: 'auto', showDesks: true, showSignOff: true, team: DEFAULT_TEAM } };
    case 'richText':
      return { id, type, visible: true, props: { html: '' } };
    case 'image':
      return { id, type, visible: true, props: { url: '', width: 'content' } };
    case 'button':
      return { id, type, visible: true, props: { label: '', href: '', style: 'primary', align: 'center' } };
    case 'divider':
      return { id, type, visible: true, props: { space: 'line' } };
    case 'faq':
      return { id, type, visible: true, props: { items: [{ q: '', a: '' }] } };
  }
}

/** The links a button, a pill or rich text may hold: the guest pages' rule. */
export function isTransferLink(href: string): boolean {
  const h = href.trim();
  if (h.startsWith('/')) return /^\/(guide|places|tours|gifts|gift-list|pickup|p)(\/[a-z0-9-]{1,64})?\/?$/.test(h);
  if (/^mailto:/i.test(h)) return /^mailto:[^@\s<>"]{1,64}@[^@\s<>"]{1,190}$/i.test(h);
  if (/^tel:/i.test(h)) return /^tel:\+?[0-9 ()-]{3,24}$/i.test(h);
  return /^https:\/\/[^\s/]+/i.test(h);
}

/** The messages between the transfer page editor and its preview frame (/_transfer-preview, same origin only). */
export const TRANSFER_PREVIEW_READY = 'amassa:transfer-preview:ready';
export const TRANSFER_PREVIEW_VIEW = 'amassa:transfer-preview:view';
export const TRANSFER_PREVIEW_SELECT = 'amassa:transfer-preview:select';
export const TRANSFER_PREVIEW_PATH = '/_transfer-preview';

/** What the editor posts to its frame: the sample view (layout included) and which block is selected. */
export interface TransferPreviewMessage {
  type: typeof TRANSFER_PREVIEW_VIEW;
  view: PublicTransferView;
  selectedId: string | null;
}
